import { motion } from "framer-motion";
import { Check, TrendingUp, TrendingDown } from "lucide-react";

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
  >
    {children}
  </motion.div>
);

/* ---------------- Data section ---------------- */
const METRICS = [
  { label: "Ad Spend",            value: "$84,200",  delta: null,     tone: "neutral" },
  { label: "Registrations",       value: "12,480",   delta: "+11%",   tone: "up"      },
  { label: "FTDs",                value: "1,872",    delta: "+18%",   tone: "up"      },
  { label: "Cost per FTD",        value: "$44.98",   delta: "−12%",   tone: "up"      },
  { label: "Deposit Value",       value: "$196,400", delta: "+24%",   tone: "up"      },
  { label: "Repeat Deposit Rate", value: "41%",      delta: "+5pts",  tone: "up"      },
  { label: "Withdrawals",         value: "$58,900",  delta: null,     tone: "neutral" },
  { label: "Net Deposits",        value: "$137,500", delta: "+22%",   tone: "up"      },
  { label: "LTV",                 value: "$104.90",  delta: "+9%",    tone: "up"      },
  { label: "ROAS",                value: "2.33x",    delta: "+0.4",   tone: "up"      },
];

const FUNNEL = [
  { stage: "Clicks",          width: "100%", value: "142,300" },
  { stage: "Registrations",   width: "72%",  value: "12,480"  },
  { stage: "FTDs",            width: "44%",  value: "1,872"   },
  { stage: "Repeat Deposits", width: "28%",  value: "764"     },
];

export default function DataSection() {
  return (
    <section id="data" className="py-20 md:py-28 bg-[#FAFAF7] scroll-mt-20" data-testid="data-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl mb-12 md:mb-16">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7C3AED] font-heading mb-4 md:mb-5">Full-funnel visibility</p>
          <h2 className="font-heading text-[1.75rem] sm:text-4xl lg:text-[2.75rem] font-bold text-[#0A0A0A] leading-[1.15]">
            From ad click to player value: full-funnel sweepstakes attribution.
          </h2>
          <p className="text-[15px] md:text-lg text-[#525252] mt-4 md:mt-5 leading-relaxed">
            One view from spend to net deposits — the numbers that actually decide whether you scale.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="bg-white border border-[#E7E5E4] rounded-2xl p-4 md:p-8 shadow-[0_20px_60px_-30px_rgba(10,10,10,0.12)]" data-testid="data-dashboard">
            {/* Header */}
            <div className="flex items-center justify-between mb-5 md:mb-7 pb-4 md:pb-5 border-b border-[#F0EDE6]">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="w-2 h-2 rounded-full bg-[#059669] shrink-0" />
                <span className="text-[11px] md:text-xs uppercase tracking-[0.18em] text-[#525252] font-heading font-semibold truncate">Player Value Dashboard</span>
              </div>
              <span className="text-[9px] md:text-[10px] text-[#A8A29E] uppercase tracking-widest border border-[#E7E5E4] rounded-full px-2 md:px-3 py-0.5 md:py-1 shrink-0">30d</span>
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 md:gap-3">
              {METRICS.map((m, i) => (
                <motion.div
                  key={m.label}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.04, duration: 0.5 }}
                  className="border border-[#EAE7E1] rounded-lg bg-[#FAFAF7] p-3 md:p-4"
                  data-testid={`data-metric-${m.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <p className="text-[9px] md:text-[10px] uppercase tracking-widest text-[#737373] font-medium mb-1.5 md:mb-2 truncate">{m.label}</p>
                  <p className="font-heading text-base md:text-lg font-bold text-[#0A0A0A] leading-none tabular-nums">{m.value}</p>
                  {m.delta && (
                    <span className="inline-flex items-center gap-1 text-[9px] md:text-[10px] font-semibold text-[#059669] mt-1.5 md:mt-2 tabular-nums">
                      {m.tone === "up" ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                      {m.delta}
                    </span>
                  )}
                </motion.div>
              ))}
            </div>

            {/* Funnel */}
            <div className="mt-6 md:mt-8 pt-5 md:pt-7 border-t border-[#F0EDE6]" data-testid="data-funnel">
              <div className="flex items-center justify-between mb-4 md:mb-5">
                <p className="text-[10px] uppercase tracking-widest text-[#737373] font-semibold font-heading">Acquisition funnel</p>
                <p className="text-[10px] text-[#A8A29E] uppercase tracking-widest">Click → Deposit</p>
              </div>
              <div className="space-y-2.5 md:space-y-3">
                {FUNNEL.map((f, i) => (
                  <div key={f.stage} className="flex items-center gap-2 md:gap-4">
                    <span className="text-[11px] md:text-xs text-[#525252] font-medium w-24 md:w-36 shrink-0 truncate">{f.stage}</span>
                    <div className="flex-1 min-w-0">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: f.width }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3 + i * 0.12, duration: 0.8, ease: "easeOut" }}
                        className="h-6 md:h-7 rounded-md bg-[#7C3AED]/85"
                      />
                    </div>
                    <span className="font-heading text-[11px] md:text-xs font-bold text-[#0A0A0A] w-16 md:w-20 text-right tabular-nums">{f.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- Safer Growth ---------------- */
const SAFER_POINTS = [
  { title: "Free-to-play positioning",           text: "Creatives and landers built around entertainment value, not income promises." },
  { title: "No-purchase-necessary disclaimers",  text: "AMOE and sweepstakes terms surfaced clearly where platforms and regulators expect them." },
  { title: "No aggressive win or cash claims",   text: "Hooks that convert without the claims that get accounts flagged and ads pulled." },
  { title: "Allowed-state targeting structure",  text: "Campaign geo setup that respects excluded states from day one." },
  { title: "Creative review before launch",      text: "Every asset is checked against platform policy and category norms before it spends." },
  { title: "Landing page recommendations",       text: "Concrete guidance on rules, terms and messaging placement across your funnel." },
];

export function SaferGrowthSection() {
  return (
    <section className="py-20 md:py-28 bg-white border-y border-[#E7E5E4]" data-testid="safer-growth-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-10 lg:gap-16">
          <Reveal className="lg:col-span-2">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7C3AED] font-heading mb-4 md:mb-5">Compliance-aware</p>
            <h2 className="font-heading text-[1.75rem] sm:text-4xl lg:text-[2.5rem] font-bold text-[#0A0A0A] leading-[1.15]">
              Safer growth for a sensitive category.
            </h2>
            <p className="text-[15px] md:text-lg text-[#525252] mt-4 md:mt-5 leading-relaxed">
              Sweepstakes is a category where one careless creative can cost you an ad account. We build campaigns to keep spending — not to gamble your infrastructure.
            </p>
          </Reveal>
          <div className="lg:col-span-3 grid sm:grid-cols-2 gap-3 md:gap-4">
            {SAFER_POINTS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.05}>
                <div className="flex gap-4 bg-[#FAFAF7] border border-[#E7E5E4] rounded-xl p-4 md:p-5 h-full" data-testid={`safer-point-${i + 1}`}>
                  <span className="w-7 h-7 rounded-full bg-[#7C3AED]/10 border border-[#7C3AED]/20 flex items-center justify-center text-[#7C3AED] shrink-0 mt-0.5">
                    <Check size={14} strokeWidth={2.5} />
                  </span>
                  <div>
                    <h3 className="font-heading text-[15px] font-bold text-[#0A0A0A] mb-1.5">{p.title}</h3>
                    <p className="text-sm text-[#525252] leading-relaxed">{p.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
